import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  getTrackedCreators,
  postCreatorDecision,
  getCreatorTrackingConfig,
  putCreatorTrackingConfig,
  reapplyCreatorTracking,
  getArmingEntities,
} from '../services/api';
import NavRail from '../components/NavRail';
import TokenDrillPanel from '../components/TokenDrillPanel';
import usePersistedState from '../hooks/usePersistedState';
import CopyBtn from '../components/CopyBtn';

/* ============================================================
   CRÉATEURS SUIVIS — Backoffice bot memecoin Solana
   Source : creator_outcomes (PG) + SET Redis creator:tracked:profitable:shadow
   Colonnes : créateur, pump_rate_30d, pump_rate_7d, pnl_7d, n_tokens, récence, tracked
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
:root{--bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;--text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;}
.cr *{box-sizing:border-box;margin:0;padding:0}
.cr,.cr *{font-family:'JetBrains Mono',ui-monospace,monospace;}
.cr{background:var(--bg);color:var(--text);height:100vh;display:flex;overflow:hidden;font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}
.cr .sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;display:flex;flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.cr .sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;margin-bottom:14px;box-shadow:0 0 14px rgba(255,59,92,.45);}
.cr .sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--text2);cursor:pointer;position:relative;transition:all .12s;font-size:16px;border:none;background:transparent;}
.cr .sb-item:hover{background:var(--surface);color:var(--text);}
.cr .sb-item.on{background:var(--surface);color:var(--amber);box-shadow:inset 2px 0 0 var(--amber);}
.cr .sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.cr .sb-item:hover .tip{opacity:1;}
.cr .sb-spacer{flex:1;}
.cr .main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.cr .top{display:flex;align-items:center;gap:18px;padding:11px 18px;border-bottom:1px solid var(--border);background:#0c0d11;flex:0 0 auto;}
.cr .top-title{font-size:14px;font-weight:800;letter-spacing:.04em;color:var(--text);}
.cr .top-spacer{flex:1;}
.cr .clock{font-size:11px;color:var(--text2);text-align:right;}
.cr .clock b{color:var(--text);font-weight:600;}
.cr .page{flex:1;overflow:auto;padding:16px;}
.cr .bar-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;gap:12px;flex-wrap:wrap;}
.cr .h1{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.cr .strip{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:16px;}
@media(max-width:900px){.cr .strip{grid-template-columns:repeat(2,1fr);}}
.cr .kpi{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:11px 13px;}
.cr .kpi .k{font-size:9.5px;color:var(--text2);letter-spacing:.07em;text-transform:uppercase;}
.cr .kpi .v{font-size:20px;font-weight:800;margin-top:4px;}
.cr .kpi .v.cy{color:var(--cyan);}
.cr .kpi .v.am{color:var(--amber);}
.cr .kpi .v.gr{color:var(--green);}
.cr .sort-row{display:flex;align-items:flex-end;gap:12px;margin-bottom:12px;flex-wrap:wrap;}
.cr .fgrp{display:flex;align-items:center;gap:6px;}
.cr .sort-lbl{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.cr .sort-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:10px;font-weight:600;padding:5px 11px;border-radius:6px;cursor:pointer;letter-spacing:.04em;transition:all .12s;}
.cr .sort-btn.on{background:var(--surface2);color:var(--amber);border-color:var(--amber);}
.cr .sort-btn:hover{color:var(--text);}
.cr .tbl-wrap{background:var(--surface);border:1px solid var(--border);border-radius:12px;overflow:hidden;}
.cr table{width:100%;border-collapse:collapse;}
.cr thead th{font-size:9px;letter-spacing:.09em;text-transform:uppercase;color:var(--text2);text-align:left;padding:8px 12px;font-weight:600;background:#0e1014;}
.cr thead th.num{text-align:right;}
.cr tbody td{padding:9px 12px;font-size:11.5px;border-top:1px solid #16191f;vertical-align:middle;}
.cr tbody td.num{text-align:right;font-variant-numeric:tabular-nums;}
.cr tbody tr:hover{background:var(--surface2);}
.cr .addr{font-size:10px;color:var(--text2);font-family:'JetBrains Mono',monospace;letter-spacing:0;}
.cr .badge{font-size:9.5px;font-weight:700;padding:2px 8px;border-radius:5px;display:inline-block;}
.cr .badge.tracked{background:rgba(255,176,32,.16);color:var(--amber);}
.cr .badge.untracked{background:rgba(138,143,152,.13);color:var(--text2);}
.cr .pos{color:var(--green);}
.cr .neg{color:var(--red);}
.cr .muted{color:var(--text2);}
.cr .loading{color:var(--text2);padding:24px;font-size:12.5px;}
.cr .err-banner{font-size:11px;color:var(--amber);margin-bottom:12px;padding:8px 12px;border-radius:8px;background:rgba(255,176,32,.10);border:1px solid rgba(255,176,32,.35);}
.cr .nodata{display:flex;align-items:center;justify-content:center;min-height:80px;font-size:11.5px;color:var(--text2);border:1px dashed var(--border);border-radius:8px;}
.cr .badge.observe{background:rgba(34,211,238,.15);color:var(--cyan);}
.cr .badge.armed{background:rgba(0,224,138,.15);color:var(--green);}
.cr .badge.rejected{background:rgba(255,59,92,.13);color:var(--red);}
.cr .badge.none{background:rgba(138,143,152,.10);color:var(--text2);}
.cr .act-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:9.5px;font-weight:700;padding:3px 8px;border-radius:5px;cursor:pointer;transition:all .12s;letter-spacing:.02em;}
.cr .act-btn:hover{color:var(--text);border-color:var(--text2);}
.cr .act-btn.obs:hover{color:var(--cyan);border-color:var(--cyan);}
.cr .act-btn.arm:hover{color:var(--green);border-color:var(--green);}
.cr .act-btn.rej:hover{color:var(--red);border-color:var(--red);}
.cr .act-btn:disabled{opacity:.4;cursor:default;}
.cr .popover{position:absolute;z-index:100;background:#0e1014;border:1px solid var(--border);border-radius:8px;padding:10px 12px;display:flex;flex-direction:column;gap:7px;min-width:220px;box-shadow:0 6px 24px rgba(0,0,0,.55);}
.cr .popover-row{display:flex;align-items:center;gap:7px;}
.cr .pop-lbl{font-size:9px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;min-width:70px;}
.cr .pop-in{background:none;border:1px solid var(--border);color:var(--text);font-family:inherit;font-size:10px;font-weight:600;padding:4px 7px;border-radius:5px;outline:none;width:80px;}
.cr .pop-in:focus{border-color:var(--green);}
.cr .arm-chip{font-size:9px;font-weight:800;padding:2px 7px;border-radius:5px;margin-left:6px;letter-spacing:.04em;white-space:nowrap;}
.cr .arm-chip.buy{background:rgba(0,224,138,.18);color:var(--green);box-shadow:0 0 8px rgba(0,224,138,.25);}
.cr .arm-chip.obs{background:rgba(34,211,238,.14);color:var(--cyan);}
.cr .cfg-section{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:14px 16px;margin-bottom:18px;}
.cr .cfg-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:8px;margin-bottom:10px;}
.cr .cfg-row{display:flex;align-items:center;gap:8px;}
.cr .cfg-lbl{font-size:9.5px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;flex:1;}
.cr .cfg-in{background:none;border:1px solid var(--border);color:var(--text);font-family:inherit;font-size:10px;font-weight:600;padding:4px 7px;border-radius:5px;outline:none;width:90px;}
.cr .cfg-in:focus{border-color:var(--amber);}
.cr .ok-banner{font-size:11px;color:var(--green);margin-left:10px;}
`;


// Mapping en-tête → clé sort backend (null = non triable)
const COL_SORT = {
  creator: null,
  pump_30d: 'pump_rate_30pct_30d',
  pump_7d: 'pump_rate_30pct_7d',
  pump_all: 'pump_rate_30pct',
  wr: 'pump_rate_30pct',
  peak_median: 'median_peak_gain_pct',
  pnl_7d: 'our_pnl_sol_7d',
  pnl_total: 'our_pnl_sol_total',
  tokens_30d: 'n_tokens_30d',
  tokens_total: 'n_tokens_total',
  recence: 'days_since_last_token',
  rug: 'rug_rate',
  statut: null,
};

function fmt(v, decimals = 1) {
  if (v === null || v === undefined) return <span style={{ color: 'var(--text2)' }}>—</span>;
  return v.toFixed(decimals);
}

function fmtPnl(v) {
  if (v === null || v === undefined) return <span style={{ color: 'var(--text2)' }}>—</span>;
  const cls = v > 0 ? 'pos' : v < 0 ? 'neg' : 'muted';
  return <span className={cls}>{v > 0 ? '+' : ''}{v.toFixed(3)}</span>;
}

function fmtAddr(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function fmtDays(d) {
  if (d === null || d === undefined) return <span style={{ color: 'var(--text2)' }}>—</span>;
  if (d < 1) return <span style={{ color: 'var(--green)' }}>&lt;1j</span>;
  if (d < 7) return <span style={{ color: 'var(--amber)' }}>{Math.round(d)}j</span>;
  return <span style={{ color: 'var(--text2)' }}>{Math.round(d)}j</span>;
}

const TRACKING_CONFIG_FIELDS = [
  { key: 'pump_rate_30pct_30d_min', label: 'pump rate 30j min (%)', type: 'number' },
  { key: 'our_pnl_sol_7d_min', label: 'PnL SOL 7j min', type: 'number' },
  { key: 'days_since_last_token_max', label: 'récence max (jours)', type: 'number' },
  { key: 'n_tokens_30d_min', label: 'tokens 30j min', type: 'number' },
];

function CreatorsPage() {
  const qc = useQueryClient();

  const [sort, setSort] = usePersistedState('creators.sort', 'pump_rate_30pct_30d');
  const [sortDir, setSortDir] = usePersistedState('creators.sortDir', 'desc');
  const [minPeakMedian, setMinPeakMedian] = usePersistedState('creators.minPeakMedian', null);
  const [minWr, setMinWr] = usePersistedState('creators.minWr', null);
  const [maxRug, setMaxRug] = usePersistedState('creators.maxRug', null);
  const [trackedOnly, setTrackedOnly] = usePersistedState('creators.trackedOnly', false);
  const [minTokens, setMinTokens] = usePersistedState('creators.minTokens', null);
  const [migrated, setMigrated] = usePersistedState('creators.migrated', null);
  const [almostBonded, setAlmostBonded] = usePersistedState('creators.almostBonded', false);
  const [armState, setArmState] = usePersistedState('creators.armState', null); // null=Tous | 'armed' | 'observe' | 'none'
  const [search, setSearch] = useState('');
  const [drillCreator, setDrillCreator] = useState(null);
  const [drillBacktest, setDrillBacktest] = useState(false);
  const [clock, setClock] = useState(new Date());

  // B5: armed popover state { [addr]: { open, size_mult, size_cap_sol } }
  const [armedPopover, setArmedPopover] = useState({});
  // B5: tracking config draft
  const [cfgDraft, setCfgDraft] = useState(null);
  const [actionErr, setActionErr] = useState(null);

  useEffect(() => {
    const i = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  // B5: Tracking config query
  const cfgQ = useQuery({
    queryKey: ['creator-tracking-config'],
    queryFn: getCreatorTrackingConfig,
  });
  const cfg = cfgQ.data || {};

  const armQ = useQuery({ queryKey: ['arming-entities'], queryFn: getArmingEntities, refetchInterval: 30000 });
  const armedByAddr = useMemo(() => {
    const m = {};
    (armQ.data?.entities || []).forEach((e) => { if (e.kind === 'creator' && e.active) m[e.address] = e; });
    return m;
  }, [armQ.data]);

  // Sync cfgDraft once cfg loads
  useEffect(() => {
    if (cfgQ.data && !cfgDraft) setCfgDraft({ ...cfgQ.data });
  }, [cfgQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // B5: mutations
  const mDecision = useMutation({
    mutationFn: ({ addr, body }) => postCreatorDecision(addr, body),
    onSuccess: () => {
      setActionErr(null);
      qc.invalidateQueries({ queryKey: ['tracked-creators'] });
      qc.invalidateQueries({ queryKey: ['arming-entities'] });
    },
    onError: (e) => setActionErr(e?.response?.data?.detail || e?.message || 'Erreur inconnue'),
  });

  const mCfg = useMutation({
    mutationFn: putCreatorTrackingConfig,
    onSuccess: () => {
      setActionErr(null);
      qc.invalidateQueries({ queryKey: ['creator-tracking-config'] });
    },
    onError: (e) => setActionErr(e?.response?.data?.detail || e?.message || 'Erreur inconnue'),
  });

  const mReapply = useMutation({
    mutationFn: reapplyCreatorTracking,
    onSuccess: () => {
      setActionErr(null);
      qc.invalidateQueries({ queryKey: ['tracked-creators'] });
      qc.invalidateQueries({ queryKey: ['arming-entities'] });
    },
    onError: (e) => setActionErr(e?.response?.data?.detail || e?.message || 'Erreur inconnue'),
  });

  // B5: popover helpers
  const openArmed = (addr) =>
    setArmedPopover((s) => ({ ...s, [addr]: { open: true, size_mult: '', size_cap_sol: '' } }));
  const closeArmed = (addr) =>
    setArmedPopover((s) => ({ ...s, [addr]: { ...s[addr], open: false } }));
  const patchArmed = (addr, patch) =>
    setArmedPopover((s) => ({ ...s, [addr]: { ...s[addr], ...patch } }));

  const doDecision = (addr, status) => {
    const body = { status };
    if (status === 'armed') {
      const st = armedPopover[addr] || {};
      if (st.size_mult !== '' && st.size_mult != null) body.size_mult = Number(st.size_mult);
      if (st.size_cap_sol !== '' && st.size_cap_sol != null) body.size_cap_sol = Number(st.size_cap_sol);
      closeArmed(addr);
    }
    mDecision.mutate({ addr, body });
  };

  const saveCfg = () => { if (cfgDraft) mCfg.mutate(cfgDraft); };

  const onSort = (colKey) => {
    const backendCol = COL_SORT[colKey];
    if (!backendCol) return;
    if (sort === backendCol) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(backendCol);
      setSortDir('desc');
    }
  };

  const sortIndicator = (colKey) => {
    const backendCol = COL_SORT[colKey];
    if (!backendCol || sort !== backendCol) return null;
    return <span style={{ marginLeft: 3 }}>{sortDir === 'desc' ? '▼' : '▲'}</span>;
  };

  const thStyle = (colKey) => ({
    cursor: COL_SORT[colKey] ? 'pointer' : 'default',
    userSelect: 'none',
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tracked-creators', sort, sortDir, minPeakMedian, minWr, maxRug, trackedOnly, minTokens, migrated, almostBonded, armState],
    queryFn: () => getTrackedCreators({ sort, sortDir, minPeakMedian, minWr, maxRug, trackedOnly, minTokens, migrated, almostBonded, armState, limit: 200, skip: 0 }),
    refetchInterval: 120000,
    placeholderData: keepPreviousData,
  });

  const creators = data?.creators || [];

  const filteredCreators = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return creators;
    return creators.filter((c) => (c.creator_address || '').toLowerCase().includes(q));
  }, [creators, search]);

  const total = data?.total || 0;
  const trackedSize = data?.tracked_set_size || 0;
  const trackedCount = creators.filter(c => c.tracked).length;
  const withData30d = creators.filter(c => c.pump_rate_30pct_30d !== null).length;

  const t = clock.toLocaleTimeString('fr-FR', { hour12: false });

  const rail = <NavRail />;

  return (
    <div className="cr">
      <style>{CSS}</style>
      {rail}
      <div className="main">
        <div className="top">
          <span className="top-title">Créateurs suivis</span>
          <div className="top-spacer" />
          <div className="clock"><b>{t}</b></div>
        </div>
        <div className="page">
          {/* KPIs */}
          <div className="strip">
            <div className="kpi">
              <div className="k">Créateurs DB</div>
              <div className="v cy">{total}</div>
            </div>
            <div className="kpi">
              <div className="k">Tracked (Redis)</div>
              <div className="v am">{trackedSize}</div>
            </div>
            <div className="kpi">
              <div className="k">Avec data 30d</div>
              <div className="v gr">{withData30d}</div>
            </div>
            <div className="kpi">
              <div className="k">Affichés tracked</div>
              <div className="v">{trackedCount}</div>
            </div>
          </div>

          {isError && (
            <div className="err-banner">Erreur chargement : {error?.message || 'inconnue'}</div>
          )}

          {actionErr && (
            <div className="err-banner">Erreur action : {actionErr}</div>
          )}

          {/* B5: Formulaire seuils de tracking */}
          <div className="cfg-section">
            <div className="bar-row" style={{ marginBottom: 10 }}>
              <span className="h1">Seuils de tracking créateur</span>
            </div>
            {cfgQ.isLoading && <div className="loading" style={{ padding: '6px 0' }}>Chargement...</div>}
            {!cfgQ.isLoading && (
              <>
                <div className="cfg-grid">
                  {TRACKING_CONFIG_FIELDS.map((f) => (
                    <div key={f.key} className="cfg-row">
                      <span className="cfg-lbl">{f.label}</span>
                      <input
                        className="cfg-in"
                        type="number"
                        value={(cfgDraft || cfg)[f.key] ?? ''}
                        onChange={(e) => setCfgDraft((d) => ({
                          ...(d || cfg),
                          [f.key]: e.target.value === '' ? null : Number(e.target.value),
                        }))}
                      />
                    </div>
                  ))}
                </div>
                {mCfg.isError && (
                  <div style={{ color: 'var(--red)', fontSize: 11, marginBottom: 6 }}>
                    Erreur 422 : {mCfg.error?.response?.data?.detail || mCfg.error?.message}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button className="sort-btn on" onClick={saveCfg} disabled={mCfg.isPending}>Enregistrer</button>
                  <button
                    className="sort-btn"
                    onClick={() => mReapply.mutate()}
                    disabled={mReapply.isPending}
                  >Réappliquer</button>
                  {mCfg.isSuccess && <span className="ok-banner">Enregistré</span>}
                  {mReapply.isSuccess && <span className="ok-banner">Réappliqué</span>}
                  {mReapply.isError && (
                    <span style={{ color: 'var(--red)', fontSize: 11 }}>
                      {mReapply.error?.response?.data?.detail || mReapply.error?.message}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Filtres */}
          <div className="bar-row">
            <span className="h1">Créateurs ({filteredCreators.length})</span>
            <div className="sort-row">
              {/* 0. Recherche adresse */}
              <div className="fgrp">
                <input
                  type="text"
                  placeholder="🔍 Adresse…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 200, background: 'none', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, padding: '6px 9px', borderRadius: 6, outline: 'none' }}
                />
                {search && <button className="sort-btn" onClick={() => setSearch('')} title="effacer">✕</button>}
              </div>

              {/* 1. toggle */}
              <div className="fgrp">
                <button
                  className={`sort-btn ${trackedOnly ? 'on' : ''}`}
                  onClick={() => setTrackedOnly(v => !v)}
                >Trackés seulement</button>
              </div>

              {/* 2. Peak médian */}
              <div className="fgrp">
                <span className="sort-lbl">Peak médian</span>
                <button
                  className={`sort-btn ${minPeakMedian === null ? 'on' : ''}`}
                  onClick={() => setMinPeakMedian(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${minPeakMedian === 50 ? 'on' : ''}`}
                  onClick={() => setMinPeakMedian(50)}
                >≥+50%</button>
                <button
                  className={`sort-btn ${minPeakMedian === 100 ? 'on' : ''}`}
                  onClick={() => setMinPeakMedian(100)}
                >≥+100%</button>
                <input
                  type="number"
                  placeholder="%"
                  value={minPeakMedian ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMinPeakMedian(val === '' ? null : Number(val));
                  }}
                  style={{
                    width: 72,
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 8px',
                    borderRadius: 6,
                    outline: 'none',
                  }}
                />
              </div>

              {/* 3. WR */}
              <div className="fgrp">
                <span className="sort-lbl">WR</span>
                <button
                  className={`sort-btn ${minWr === null ? 'on' : ''}`}
                  onClick={() => setMinWr(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${minWr === 50 ? 'on' : ''}`}
                  onClick={() => setMinWr(50)}
                >≥50%</button>
                <button
                  className={`sort-btn ${minWr === 70 ? 'on' : ''}`}
                  onClick={() => setMinWr(70)}
                >≥70%</button>
                <input
                  type="number"
                  placeholder="WR ≥ %"
                  value={minWr ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMinWr(val === '' ? null : Number(val));
                  }}
                  style={{
                    width: 80,
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 8px',
                    borderRadius: 6,
                    outline: 'none',
                  }}
                />
              </div>

              {/* 4. Rug max */}
              <div className="fgrp">
                <span className="sort-lbl">Rug max</span>
                <button
                  className={`sort-btn ${maxRug === null ? 'on' : ''}`}
                  onClick={() => setMaxRug(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${maxRug === 10 ? 'on' : ''}`}
                  onClick={() => setMaxRug(10)}
                >≤10%</button>
                <button
                  className={`sort-btn ${maxRug === 25 ? 'on' : ''}`}
                  onClick={() => setMaxRug(25)}
                >≤25%</button>
                <input
                  type="number"
                  placeholder="rug ≤ %"
                  value={maxRug ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMaxRug(val === '' ? null : Number(val));
                  }}
                  style={{
                    width: 80,
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 8px',
                    borderRadius: 6,
                    outline: 'none',
                  }}
                />
              </div>

              {/* 5. Nb tokens */}
              <div className="fgrp">
                <span className="sort-lbl">Nb tokens</span>
                <button
                  className={`sort-btn ${minTokens === null ? 'on' : ''}`}
                  onClick={() => setMinTokens(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${minTokens === 5 ? 'on' : ''}`}
                  onClick={() => setMinTokens(5)}
                >≥5</button>
                <button
                  className={`sort-btn ${minTokens === 20 ? 'on' : ''}`}
                  onClick={() => setMinTokens(20)}
                >≥20</button>
                <input
                  type="number"
                  placeholder="tokens ≥"
                  value={minTokens ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMinTokens(val === '' ? null : Number(val));
                  }}
                  style={{
                    width: 80,
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 8px',
                    borderRadius: 6,
                    outline: 'none',
                  }}
                />
              </div>

              {/* ligne 2 — Migré + Almost bonded sur une nouvelle ligne */}
              <div style={{ flexBasis: '100%', height: 0 }} />

              {/* 6. Migré */}
              <div className="fgrp">
                <span className="sort-lbl">Migré</span>
                <button
                  className={`sort-btn ${migrated === null ? 'on' : ''}`}
                  onClick={() => setMigrated(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${migrated === 'true' ? 'on' : ''}`}
                  onClick={() => setMigrated('true')}
                >A migré</button>
                <button
                  className={`sort-btn ${migrated === 'false' ? 'on' : ''}`}
                  onClick={() => setMigrated('false')}
                >Jamais</button>
              </div>

              {/* extra : Almost bonded (hors ordre imposé, conservé) */}
              <div className="fgrp">
                <button
                  className={`sort-btn ${almostBonded ? 'on' : ''}`}
                  onClick={() => setAlmostBonded(v => !v)}
                >Almost bonded</button>
              </div>

              {/* 7. Armement (lu depuis /api/arming/entities = tracked_entities, client-side) */}
              <div className="fgrp">
                <span className="sort-lbl">Armement</span>
                <button
                  className={`sort-btn ${armState === null ? 'on' : ''}`}
                  onClick={() => setArmState(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${armState === 'armed' ? 'on' : ''}`}
                  onClick={() => setArmState('armed')}
                >⚡ Armé</button>
                <button
                  className={`sort-btn ${armState === 'observe' ? 'on' : ''}`}
                  onClick={() => setArmState('observe')}
                >👁 Observé</button>
                <button
                  className={`sort-btn ${armState === 'none' ? 'on' : ''}`}
                  onClick={() => setArmState('none')}
                >Non suivi</button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="loading">Chargement...</div>
          ) : filteredCreators.length === 0 ? (
            <div className="nodata">Aucun créateur en base (analyze_token_outcomes non lancé ?)</div>
          ) : (
            <div className="tbl-wrap">
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table>
                <thead>
                  <tr>
                    <th style={thStyle('creator')} onClick={() => onSort('creator')}>Créateur</th>
                    <th className="num" style={thStyle('pump_30d')} onClick={() => onSort('pump_30d')}>Pump% 30d{sortIndicator('pump_30d')}</th>
                    <th className="num" style={thStyle('pump_7d')} onClick={() => onSort('pump_7d')}>Pump% 7d{sortIndicator('pump_7d')}</th>
                    <th className="num" style={thStyle('pump_all')} onClick={() => onSort('pump_all')}>Pump% all{sortIndicator('pump_all')}</th>
                    <th className="num" style={thStyle('wr')} onClick={() => onSort('wr')}>WR{sortIndicator('wr')}</th>
                    <th className="num" style={thStyle('peak_median')} onClick={() => onSort('peak_median')}>Peak médian{sortIndicator('peak_median')}</th>
                    <th className="num" style={thStyle('pnl_7d')} onClick={() => onSort('pnl_7d')}>PnL 7d{sortIndicator('pnl_7d')}</th>
                    <th className="num" style={thStyle('pnl_total')} onClick={() => onSort('pnl_total')}>PnL total{sortIndicator('pnl_total')}</th>
                    <th className="num" style={thStyle('tokens_30d')} onClick={() => onSort('tokens_30d')}>Tokens 30d{sortIndicator('tokens_30d')}</th>
                    <th className="num" style={thStyle('tokens_total')} onClick={() => onSort('tokens_total')}>Tokens total{sortIndicator('tokens_total')}</th>
                    <th className="num" style={thStyle('recence')} onClick={() => onSort('recence')}>Récence{sortIndicator('recence')}</th>
                    <th className="num" style={thStyle('rug')} onClick={() => onSort('rug')}>Rug%{sortIndicator('rug')}</th>
                    <th style={thStyle('statut')}>Statut</th>
                    <th>Décision</th>
                    <th>Actions</th>
                    <th>Backtest</th>
                    <th>Liens</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCreators.map(c => (
                    <tr key={c.creator_address} style={{ cursor: 'pointer' }} onClick={() => { setDrillBacktest(false); setDrillCreator(c.creator_address); }}>
                      <td>
                        {c.tracked && <span style={{ color: 'var(--amber)', marginRight: 5, fontSize: 11 }}>●</span>}
                        <span
                          className="addr"
                          title={c.creator_address}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(c.creator_address); }}
                        >{fmtAddr(c.creator_address)}</span>
                        <CopyBtn text={c.creator_address} />
                        {(() => {
                          const e = armedByAddr[c.creator_address];
                          if (!e) return null;
                          if (e.mode === 'buy') return <span className="arm-chip buy" title={`armé ×${e.size_mult ?? '?'} cap ${e.size_cap_sol ?? '∞'} SOL`}>⚡ ARMÉ{e.size_mult != null ? ` ×${e.size_mult}` : ''}</span>;
                          return <span className="arm-chip obs" title="observé">👁 OBS</span>;
                        })()}
                      </td>
                      <td className="num">{c.pump_rate_30pct_30d !== null ? `${fmt(c.pump_rate_30pct_30d)}%` : <span className="muted">—</span>}</td>
                      <td className="num">{c.pump_rate_30pct_7d !== null ? `${fmt(c.pump_rate_30pct_7d)}%` : <span className="muted">—</span>}</td>
                      <td className="num">{c.pump_rate_30pct !== null ? `${fmt(c.pump_rate_30pct)}%` : <span className="muted">—</span>}</td>
                      <td className="num">{c.pump_rate_30pct !== null ? `${fmt(c.pump_rate_30pct)}%` : <span className="muted">—</span>}</td>
                      <td className="num">{c.median_peak_gain_pct != null ? <span className="pos">+{fmt(c.median_peak_gain_pct)}%</span> : <span className="muted">—</span>}</td>
                      <td className="num">{fmtPnl(c.our_pnl_sol_7d)}</td>
                      <td className="num">{fmtPnl(c.our_pnl_sol_total)}</td>
                      <td className="num">{c.n_tokens_30d ?? <span className="muted">—</span>}</td>
                      <td className="num">{c.n_tokens_total}</td>
                      <td className="num">{fmtDays(c.days_since_last_token)}</td>
                      <td className="num">{c.rug_rate !== null ? `${fmt(c.rug_rate)}%` : <span className="muted">—</span>}</td>
                      <td>
                        <span className={`badge ${c.tracked ? 'tracked' : 'untracked'}`}>
                          {c.tracked ? 'tracked' : 'shadow'}
                        </span>
                      </td>
                      {/* B5: decision_status badge */}
                      <td onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const ds = c.decision_status || null;
                          const cls = ds === 'observe' ? 'observe' : ds === 'armed' ? 'armed' : ds === 'rejected' ? 'rejected' : 'none';
                          return <span className={`badge ${cls}`}>{ds || '—'}</span>;
                        })()}
                      </td>
                      {/* B5: action buttons + armed popover */}
                      <td onClick={(e) => e.stopPropagation()} style={{ position: 'relative', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const addr = c.creator_address;
                          const st = armedPopover[addr] || { open: false };
                          const busy = mDecision.isPending;
                          if (st.open) {
                            return (
                              <div className="popover" style={{ top: 0, left: 0 }}>
                                <div className="popover-row">
                                  <span className="pop-lbl">size_mult</span>
                                  <input
                                    className="pop-in"
                                    type="number"
                                    placeholder="ex: 2.0"
                                    value={st.size_mult}
                                    onChange={(e) => patchArmed(addr, { size_mult: e.target.value })}
                                  />
                                </div>
                                <div className="popover-row">
                                  <span className="pop-lbl">cap SOL</span>
                                  <input
                                    className="pop-in"
                                    type="number"
                                    placeholder="ex: 0.5"
                                    value={st.size_cap_sol}
                                    onChange={(e) => patchArmed(addr, { size_cap_sol: e.target.value })}
                                  />
                                </div>
                                <div className="popover-row" style={{ gap: 6 }}>
                                  <button className="act-btn arm" onClick={() => doDecision(addr, 'armed')} disabled={busy}>OK</button>
                                  <button className="act-btn" onClick={() => closeArmed(addr)}>Annuler</button>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="act-btn obs" onClick={() => doDecision(addr, 'observe')} disabled={busy}>Observe</button>
                              <button className="act-btn arm" onClick={() => openArmed(addr)} disabled={busy}>Armer</button>
                              <button className="act-btn rej" onClick={() => doDecision(addr, 'rejected')} disabled={busy}>Rejeter</button>
                            </div>
                          );
                        })()}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="sort-btn"
                          onClick={() => { setDrillBacktest(true); setDrillCreator(c.creator_address); }}
                        >⏱ backtest</button>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <a
                          href={`https://solscan.io/account/${c.creator_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 10, color: '#9bd1ff', marginRight: 6, textDecoration: 'none', fontWeight: 700 }}
                        >SS</a>
                        <a
                          href={`https://gmgn.ai/sol/address/${c.creator_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 10, color: 'var(--amber)', textDecoration: 'none', fontWeight: 700 }}
                        >GM</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      </div>
      {drillCreator && <TokenDrillPanel kind="creator" id={drillCreator} openBacktest={drillBacktest} onClose={() => setDrillCreator(null)} />}
    </div>
  );
}

export default CreatorsPage;
